import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import { Dispatch, useEffect, useRef, useState } from 'react';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { isValidEmail } from '../../../utils';
import toastTrigger from '@ui/toast/toast-trigger';
import CreatableReactSelect from '@ui/select/react-select/creatable-react-select';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type ConfirmUploadRegistrantsModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  setTestEmailAddressesForSend?: Dispatch<React.SetStateAction<string[]>>;
  testEmailAddresses?: string[];
  sendEmails?: (isTestEmailSend: boolean) => void;
  saveDisabled?: boolean;
  isSaving?: boolean;
};

const ConfirmUploadRegistrantsModal = ({
  open,
  setOpen,
  sendEmails,
  saveDisabled,
  setTestEmailAddressesForSend,
  isSaving,
}: ConfirmUploadRegistrantsModalProps) => {
  const [showAddTestEmails, setShowAddTestEmails] = useState(false); //test emails modal
  const [testEmailAddresses, setTestEmailAddresses] = useState<TReactSelectOption[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  useEffect(() => {
    if (setTestEmailAddressesForSend) {
      setTestEmailAddressesForSend(testEmailAddresses.map((email) => email.label));
    }
  }, [testEmailAddresses, setTestEmailAddressesForSend]);

  const handleSendEmails = () => {
    if (sendEmails) {
      sendEmails(true); //bubble up the send action
      setShowAddTestEmails(false); //close the modal
    }
  };

  const sendTestEmailsDisabled = () => {
    return !testEmailAddresses.length || isSaving;
  };

  return (
    <>
      <Modal
        open={open}
        onRequestClose={() => setOpen(false)}
        backgroundColor="var(--gray-900)"
        customSize={{
          width: '640px',
          height: 'auto',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '250px',
        }}
        RenderHeaderContent={() => {
          return (
            <Container className="flex ai-center gap-8 H4 padding-24" borderRadius="16px">
              Are you sure you want to send now?
            </Container>
          );
        }}
        RenderFooterContent={() => (
          <Container className="flex jc-space-between ai-center padding-l-40 padding-b-16" borderRadius="16px">
            <Container className="flex ai-center">
              <Button className="padding-0" onClick={() => setShowAddTestEmails(true)}>
                <Text className="flex ai-center gap-6" fontSize="14px">
                  <Icon name={ICONS.VIEWERS_EYE} size={20} /> Preview email
                </Text>
              </Button>
            </Container>
            <Container
              background="var(--gray-900)"
              className="flex jc-flex-end ai-flex-end gap-16"
              padding="8px 24px"
              borderRadius="16px"
            >
              <Button onClick={() => setOpen(false)} design="LocalBttnSec" className="L1 padding-v-16 padding-h-24">
                Cancel
              </Button>
              <Button
                onClick={() => (sendEmails ? sendEmails(false) : undefined)}
                className="L1 padding-v-16 padding-h-24"
                disabled={saveDisabled}
                disabledOpacity={0.6}
                loading={isSaving}
                backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Send
                </Text>
              </Button>
            </Container>
          </Container>
        )}
      >
        <Container className="padding-h-40">
          <Text className="P1">Be sure to check your registration confirmation email design prior to saving.</Text>
          <Text className="P1">Sending now will send the emails as is.</Text>
        </Container>
      </Modal>
      <Modal
        open={showAddTestEmails}
        customSize={{
          width: '500px',
          height: 'auto',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '250px',
        }}
        backgroundColor="var(--gray-900)"
        contentWrapperClassName="padding-24 overflow-y-auto flex-1 flex-column"
        onRequestClose={() => setShowAddTestEmails(false)}
        RenderHeaderContent={() => {
          return (
            <Container className="flex ai-center" borderRadius="16px">
              <Text className="H4">Send test email</Text>
            </Container>
          );
        }}
        RenderFooterContent={() => (
          <Container background="var(--gray-900)" className="flex jc-flex-end ai-flex-end gap-16">
            <Button
              onClick={() => {
                setShowAddTestEmails(false);
                setTestEmailAddresses([]);
              }}
              design="LocalBttnSec"
              className="L1 padding-v-16 padding-h-24"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmails}
              className="padding-v-16 padding-h-24"
              disabled={sendTestEmailsDisabled()}
              disabledOpacity={0.6}
              loading={isSaving}
              backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Send Test
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="flex-column ai-center padding-20 gap-16" background="var(--gray-900)" borderRadius="16px">
          <Text className="P1">Project must be published to reflect any email changes.</Text>
          <CreatableReactSelect
            disableClearWhenNotFocused
            placeholder="Enter emails"
            isMulti
            menuIsOpen={false}
            value={testEmailAddresses}
            ref={selectRef}
            styles={reactSelectAdminStyles({
              container: () => ({ width: '400px' }),
              control: () => ({ width: '400px' }),
              dropdownIndicator: () => ({ display: 'none' }),
            })}
            onChange={(newValue) => {
              setTestEmailAddresses(newValue as TReactSelectOption[]);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const target = e.target;
                if (target instanceof HTMLInputElement) {
                  e.preventDefault();

                  const value = target.value.trim();

                  if (!isValidEmail(value)) {
                    toastTrigger(
                      <div className="S1">
                        <div>Email entered is not a valid email address. Please try again.</div>
                      </div>,
                      {
                        type: 'error',
                        autoClose: 5000,
                      }
                    );
                    return;
                  }

                  if (!testEmailAddresses.some((email) => email.label === value)) {
                    setTestEmailAddresses((prev) => [...prev, { label: value, value: value }]);

                    const select = selectRef.current;
                    if (select) {
                      select?.onInputChange('', 'set-value');
                    }
                  }
                }
              }
            }}
          />
        </Container>
      </Modal>
    </>
  );
};

export default ConfirmUploadRegistrantsModal;
